import { useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { ContentfulFeatureFlagConnection, FeatureFlagMap } from "./types";

/**
 * Return a boolean based on whether a given feature flag is enabled.
 * Reads from local feature-flags.json file if present, or cms if not.
 * @param {string} feature The key of the feature flag
 * @returns {boolean} Status of the feature flag
 * @example
 * const isDeviceInventoryEnabled = useFeatureFlag('DEVICE_INVENTORY');
 */
 export const useFeatureFlag = (feature: string, ): boolean => {
  const { allContentfulFeatureFlag }: {allContentfulFeatureFlag: ContentfulFeatureFlagConnection} = useStaticQuery(featureFlagQuery);

  const featureFlags = useMemo(() => {
    const contentfulFlags = allContentfulFeatureFlag.edges
      .map(({ node }) => node)
      .reduce((acc, { featureKey, enabled }) => ({
        ...acc,
        [formatFeatureKey(featureKey)]: enabled
      }), {} as FeatureFlagMap);

    // Env variable set by gatsby-plugin-feature-flags
    const localFlags: FeatureFlagMap = process.env.FEATURE_FLAGS || {};

    return { ...contentfulFlags, ...localFlags };
  }, [allContentfulFeatureFlag.edges, process.env.FEATURE_FLAGS]);

  return !!featureFlags[formatFeatureKey(feature)];
};

const formatFeatureKey = (feature: string) => feature.toUpperCase().trim();

const featureFlagQuery = graphql`
  query {
    allContentfulFeatureFlag(
      filter: { node_locale: { eq: "en-CA" } }
    ) {
      edges {
        node {
          featureKey
          enabled
        }
      }
    }
  }
`;
